import { confirmDialog, notify } from '../utils/notify';
import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { taxConfigAPI } from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import { bracketProblem } from '../utils/taxBrackets';
import { can } from '../utils/permissions';
import TaxYearSection from '../components/TaxYearSection';
import TaxExemptionsSection from '../components/TaxExemptionsSection';
import { useLanguage } from '../contexts/LanguageContext';
import Modal from '../components/Modal';
import {
  CircleDollarSign, Lightbulb, Pencil, Plus, Power, PowerOff, Trash2,
} from 'lucide-react';
import StatusBadge from '../components/StatusBadge';
import './TaxConfiguration.css';

// Rows payroll does not apply as stored: AOV_SRD is informational (payroll
// applies the AOV % of the Loonbelasting year), and of LB_SRD only the
// brackets of the year count. Their minimum and maximum are not offered.
const PAYROLL_OWN = {
  AOV_SRD: 'Informational: payroll does not apply this row. It applies the AOV % of the Loonbelasting year (tax year section above).',
  LB_SRD: 'Payroll applies the brackets of the year and the Loonbelasting parameters above; a minimum or maximum does not apply.',
};

// A stored number for an input: 0 stays "0" (`value || ''` emptied it).
const toInput = (value) => (value === null || value === undefined || value === '' ? '' : String(Number(value)));

const TaxConfiguration = () => {
  const { user } = useContext(AuthContext);
  const { t, locale } = useLanguage();
  const [loading, setLoading] = useState(false);
  // The tax year shown (TaxYearSection): brackets are read and saved for it.
  const [taxYear, setTaxYear] = useState(null);
  
  // Taxes
  const [taxes, setTaxes] = useState([]);
  const [showTaxModal, setShowTaxModal] = useState(false);
  const [editingTax, setEditingTax] = useState(null);
  
  const [taxForm, setTaxForm] = useState({
    tax_name: '',
    tax_code: '',
    description: '',
    tax_type: 'percentage',
    tax_rate: '',
    applies_to: 'gross',
    currency: 'SRD',
    is_employer_contribution: false,
    employer_rate: '',
    is_active: true,
    is_mandatory: false,
    calculation_order: 0,
    min_amount: '',
    max_amount: ''
  });

  // Tax Brackets for progressive tax
  const [taxBrackets, setTaxBrackets] = useState([
    { bracket_order: 1, min_amount: '0', max_amount: '', tax_rate: '', description: '' }
  ]);

  useEffect(() => {
    loadTaxes();
    // eslint-disable-next-line
  }, []);

  const loadTaxes = async () => {
    try {
      setLoading(true);
      const response = await taxConfigAPI.getAll();
      setTaxes(response.data.taxes);
    } catch (error) {
      console.error('Error loading taxes:', error);
      notify(t('Error loading tax configurations'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const resetTaxForm = () => {
    setTaxForm({
      tax_name: '',
      tax_code: '',
      description: '',
      tax_type: 'percentage',
      tax_rate: '',
      applies_to: 'gross',
      currency: 'SRD',
      is_employer_contribution: false,
      employer_rate: '',
      is_active: true,
      is_mandatory: false,
      calculation_order: 0,
      min_amount: '',
      max_amount: ''
    });
    setTaxBrackets([
      { bracket_order: 1, min_amount: '0', max_amount: '', tax_rate: '', description: '' }
    ]);
    setEditingTax(null);
  };

  // Tax Brackets functions
  const addBracket = () => {
    // Brackets are contiguous: a new one starts where the last one ends.
    const previous = taxBrackets[taxBrackets.length - 1];
    setTaxBrackets([
      ...taxBrackets,
      { 
        bracket_order: taxBrackets.length + 1, 
        min_amount: previous ? previous.max_amount : '0', 
        max_amount: '', 
        tax_rate: '', 
        description: '' 
      }
    ]);
  };

  const removeBracket = (index) => {
    if (taxBrackets.length === 1) {
      notify(t('At least one bracket is required'), 'warning');
      return;
    }
    const newBrackets = taxBrackets.filter((_, i) => i !== index);
    // Reorder brackets
    newBrackets.forEach((bracket, i) => {
      bracket.bracket_order = i + 1;
    });
    setTaxBrackets(newBrackets);
  };

  const updateBracket = (index, field, value) => {
    const newBrackets = [...taxBrackets];
    newBrackets[index][field] = value;
    setTaxBrackets(newBrackets);
  };

  const handleEditTax = async (tax) => {
    setEditingTax(tax);
    setTaxForm({
      tax_name: tax.tax_name || '',
      tax_code: tax.tax_code || '',
      description: tax.description || '',
      tax_type: tax.tax_type || 'percentage',
      tax_rate: toInput(tax.tax_rate),
      applies_to: tax.applies_to || 'gross',
      currency: tax.currency || 'SRD',
      is_employer_contribution: tax.is_employer_contribution || false,
      employer_rate: toInput(tax.employer_rate),
      is_active: tax.is_active !== false,
      is_mandatory: tax.is_mandatory || false,
      calculation_order: tax.calculation_order || 0,
      min_amount: toInput(tax.min_amount),
      max_amount: toInput(tax.max_amount)
    });

    // Load tax brackets if tiered tax
    if (tax.tax_type === 'tiered') {
      try {
        const response = await taxConfigAPI.getBrackets(tax.id, taxYear);
        if (response.data.brackets && response.data.brackets.length > 0) {
          setTaxBrackets(response.data.brackets.map(b => ({
            id: b.id,
            bracket_order: b.bracket_order,
            min_amount: toInput(b.min_amount),
            max_amount: toInput(b.max_amount),
            tax_rate: toInput(b.tax_rate),
            description: b.description || ''
          })));
        }
      } catch (error) {
        console.error('Error loading brackets:', error);
      }
    }
    
    setShowTaxModal(true);
  };

  const handleSubmitTax = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);

      // Validate brackets for tiered tax
      if (taxForm.tax_type === 'tiered') {
        // Same rules as the server (contiguous from 0, last one open, 0 %
        // allowed), checked before the tax itself is saved.
        const problem = bracketProblem(taxBrackets);
        if (problem) {
          notify(t(problem.message, problem.vars), 'warning');
          setLoading(false);
          return;
        }
      }
      
      const cleanData = {
        ...taxForm,
        tax_rate: taxForm.tax_rate === '' ? null : parseFloat(taxForm.tax_rate),
        employer_rate: taxForm.employer_rate === '' ? null : parseFloat(taxForm.employer_rate),
        min_amount: taxForm.min_amount === '' ? null : parseFloat(taxForm.min_amount),
        max_amount: taxForm.max_amount === '' ? null : parseFloat(taxForm.max_amount),
        calculation_order: parseInt(taxForm.calculation_order) || 0
      };

      let taxId;

      if (editingTax) {
        await taxConfigAPI.update(editingTax.id, cleanData);
        taxId = editingTax.id;
        notify(t('Tax configuration updated successfully!'), 'success');
      } else {
        const response = await taxConfigAPI.create(cleanData);
        taxId = response.data.tax.id;
        notify(t('Tax configuration created successfully!'), 'success');
      }

      // Save brackets if tiered tax
      if (taxForm.tax_type === 'tiered' && taxId) {
        try {
          const bracketsData = taxBrackets.map(b => ({
            bracket_order: b.bracket_order,
            min_amount: parseFloat(b.min_amount),
            max_amount: b.max_amount !== '' && b.max_amount != null ? parseFloat(b.max_amount) : null,
            tax_rate: parseFloat(b.tax_rate),
            description: b.description || null
          }));

          await taxConfigAPI.updateBrackets(taxId, { brackets: bracketsData, config_year: taxYear });
        } catch (error) {
          console.error('Error saving brackets:', error);
          notify(t('Tax created but error saving brackets: ') + (error.response?.data?.message || error.message), 'error');
        }
      }

      setShowTaxModal(false);
      resetTaxForm();
      loadTaxes();
    } catch (error) {
      console.error('Error saving tax:', error);
      notify(error.response?.data?.message || t('Error saving tax configuration'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTax = async (id) => {
    if (!await confirmDialog(t('Are you sure you want to delete this tax configuration?'))) return;

    try {
      await taxConfigAPI.delete(id);
      notify(t('Tax configuration deleted successfully!'), 'success');
      loadTaxes();
    } catch (error) {
      console.error('Error deleting tax:', error);
      notify(t('Error deleting tax configuration'), 'error');
    }
  };

  const handleToggleTaxStatus = async (tax) => {
    // Without an active LB_SRD the server refuses every SRD payroll, so
    // switching it off is confirmed first.
    if (tax.tax_code === 'LB_SRD' && tax.is_active && !await confirmDialog(t('Deactivate Loonbelasting (LB_SRD)? No SRD payroll can be calculated while it is inactive.'))) return;
    try {
      await taxConfigAPI.toggleStatus(tax.id);
      loadTaxes();
    } catch (error) {
      console.error('Error toggling tax status:', error);
      notify(t('Error toggling tax status'), 'error');
    }
  };

  // On a card: the amount of a fixed tax, the minimum and maximum, and the
  // note of a row payroll does not apply as stored.
  const money = (tax, value) => `${tax.currency} ${Number(value).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const renderLimits = (tax) => (
    <>
      {tax.tax_type === 'fixed' && tax.tax_rate !== null && (
        <div className="tc-card__detail">
          <span className="tc-card__label">{t('Amount:')}</span>
          <span className="tc-card__value is-strong">{money(tax, tax.tax_rate)}</span>
        </div>
      )}
      {!PAYROLL_OWN[tax.tax_code] && tax.min_amount !== null && (
        <div className="tc-card__detail">
          <span className="tc-card__label">{t('Minimum base:')}</span>
          <span className="tc-card__value">{money(tax, tax.min_amount)}</span>
        </div>
      )}
      {!PAYROLL_OWN[tax.tax_code] && tax.max_amount !== null && (
        <div className="tc-card__detail">
          <span className="tc-card__label">{t('Maximum tax:')}</span>
          <span className="tc-card__value">{money(tax, tax.max_amount)}</span>
        </div>
      )}
      {tax.tax_code === 'AOV_SRD' && (
        <div className="tc-card__detail is-info">
          <StatusBadge variant="info">{t('Informational')}</StatusBadge>
          <span className="tc-card__value">{t(PAYROLL_OWN.AOV_SRD)}</span>
        </div>
      )}
    </>
  );

  // tax_configuration (role_permissions): finance_manager creates and edits,
  // hr_manager only looks, only super_admin deletes.
  const canCreate = can(user, 'tax_configuration', 'create');
  const canManage = can(user, 'tax_configuration', 'update');
  const canDelete = can(user, 'tax_configuration', 'delete');

  // Group taxes by currency
  const taxesByUSD = taxes.filter(t => t.currency === 'USD');
  const taxesBySRD = taxes.filter(t => t.currency === 'SRD');

  return (
    <div className="taxconfig-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('Tax Configuration')}</h1>
          <p className="page-sub">{t('Manage taxes')}</p>
        </div>
        {canCreate && (
          <button
            onClick={() => {
              resetTaxForm();
              setShowTaxModal(true);
            }}
            className="btn btn--primary tc-create"
          >
            <Plus aria-hidden="true" /> {t('Create {type}', { type: t('Tax') })}
          </button>
        )}
      </div>

      {/* Deductions and benefits have their own pages, on the catalogs payroll reads */}
      <p className="tc-links">
        {t('Deductions and benefits are managed on their own pages:')}{' '}
        <Link to="/deductions">{t('Deductions')}</Link>{' · '}
        <Link to="/benefits">{t('Benefits')}</Link>
      </p>

      {/* The year: Loonbelasting parameters, and the year whose brackets are edited below */}
      <TaxYearSection onYearChange={setTaxYear} />
      {taxYear && <TaxExemptionsSection year={taxYear} canEdit={canManage} />}

      {/* TAXES */}
        <div className="tc-content">
          {loading ? (
            <div className="empty">{t('Loading...')}</div>
          ) : (
            <>
              {/* USD Taxes */}
              {taxesByUSD.length > 0 && (
                <div className="tc-currency">
                  <h2 className="tc-currency__title"><CircleDollarSign aria-hidden="true" /> {t('USD Taxes')}</h2>
                  <div className="tc-grid">
                    {taxesByUSD.map(tax => (
                      <div key={tax.id} className={`tc-card${!tax.is_active ? ' is-inactive' : ''}`}>
                        <div className="tc-card__head">
                          <div>
                            <h3>{tax.tax_name}</h3>
                            <span className="tc-card__code">{tax.tax_code}</span>
                          </div>
                          <div className="tc-card__actions">
                            {canManage && (
                              <>
                                <button 
                                  type="button"
                                  className="btn btn--secondary btn--icon"
                                  onClick={() => handleEditTax(tax)}
                                  title={t('Edit')}
                                  aria-label={t('Edit')}
                                >
                                  <Pencil aria-hidden="true" />
                                </button>
                                <button
                                  type="button"
                                  className="btn btn--secondary btn--icon"
                                  onClick={() => handleToggleTaxStatus(tax)}
                                  title={tax.is_active ? t('Deactivate') : t('Activate')}
                                  aria-label={tax.is_active ? t('Deactivate') : t('Activate')}
                                >
                                  {tax.is_active ? <PowerOff aria-hidden="true" /> : <Power aria-hidden="true" />}
                                </button>
                              </>
                            )}
                            {canDelete && (
                              <button
                                type="button"
                                className="btn btn--secondary btn--icon btn--danger-text"
                                onClick={() => handleDeleteTax(tax.id)}
                                title={t('Delete')}
                                aria-label={t('Delete')}
                              >
                                <Trash2 aria-hidden="true" />
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="tc-card__body">
                          {tax.description && (
                            <p className="tc-card__description">{tax.description}</p>
                          )}

                          <div className="tc-card__details">
                            <div className="tc-card__detail">
                              <span className="tc-card__label">{t('Type:')}</span>
                              <span className="tc-card__value">
                                {tax.tax_type === 'percentage' && t('Percentage')}
                                {tax.tax_type === 'fixed' && t('Fixed')}
                                {tax.tax_type === 'tiered' && t('Tiered/Progressive')}
                              </span>
                            </div>

                            {tax.tax_type === 'percentage' && (
                              <div className="tc-card__detail">
                                <span className="tc-card__label">{t('Rate:')}</span>
                                <span className="tc-card__value is-strong">{tax.tax_rate}%</span>
                              </div>
                            )}

                            {tax.tax_type === 'tiered' && (
                              <div className="tc-card__detail">
                                <span className="tc-card__label">{t('Structure:')}</span>
                                <span className="tc-card__value">{t('Progressive Brackets')}</span>
                              </div>
                            )}

                            {renderLimits(tax)}

                            <div className="tc-card__detail">
                              <span className="tc-card__label">{t('Applies to:')}</span>
                              <span className="tc-card__value">{t(tax.applies_to)}</span>
                            </div>

                            {tax.is_employer_contribution && (
                              <div className="tc-card__detail">
                                <span className="tc-card__label">{t('Employer:')}</span>
                                <span className="tc-card__value">{tax.employer_rate}%</span>
                              </div>
                            )}

                            {tax.is_mandatory && (
                              <div className="tc-card__detail">
                                <StatusBadge variant="neutral">{t('Mandatory')}</StatusBadge>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* SRD Taxes */}
              {taxesBySRD.length > 0 && (
                <div className="tc-currency">
                  <h2 className="tc-currency__title"><CircleDollarSign aria-hidden="true" /> {t('SRD Taxes')}</h2>
                  <div className="tc-grid">
                    {taxesBySRD.map(tax => (
                      <div key={tax.id} className={`tc-card${!tax.is_active ? ' is-inactive' : ''}`}>
                        {/* Same card structure as USD */}
                        <div className="tc-card__head">
                          <div>
                            <h3>{tax.tax_name}</h3>
                            <span className="tc-card__code">{tax.tax_code}</span>
                          </div>
                          <div className="tc-card__actions">
                            {canManage && (
                              <>
                                <button 
                                  type="button"
                                  className="btn btn--secondary btn--icon"
                                  onClick={() => handleEditTax(tax)}
                                  title={t('Edit')}
                                  aria-label={t('Edit')}
                                >
                                  <Pencil aria-hidden="true" />
                                </button>
                                <button
                                  type="button"
                                  className="btn btn--secondary btn--icon"
                                  onClick={() => handleToggleTaxStatus(tax)}
                                  title={tax.is_active ? t('Deactivate') : t('Activate')}
                                  aria-label={tax.is_active ? t('Deactivate') : t('Activate')}
                                >
                                  {tax.is_active ? <PowerOff aria-hidden="true" /> : <Power aria-hidden="true" />}
                                </button>
                              </>
                            )}
                            {canDelete && (
                              <button
                                type="button"
                                className="btn btn--secondary btn--icon btn--danger-text"
                                onClick={() => handleDeleteTax(tax.id)}
                                title={t('Delete')}
                                aria-label={t('Delete')}
                              >
                                <Trash2 aria-hidden="true" />
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="tc-card__body">
                          {tax.description && (
                            <p className="tc-card__description">{tax.description}</p>
                          )}

                          <div className="tc-card__details">
                            <div className="tc-card__detail">
                              <span className="tc-card__label">{t('Type:')}</span>
                              <span className="tc-card__value">
                                {tax.tax_type === 'percentage' && t('Percentage')}
                                {tax.tax_type === 'fixed' && t('Fixed')}
                                {tax.tax_type === 'tiered' && t('Tiered/Progressive')}
                              </span>
                            </div>

                            {tax.tax_type === 'percentage' && (
                              <div className="tc-card__detail">
                                <span className="tc-card__label">{t('Rate:')}</span>
                                <span className="tc-card__value is-strong">{tax.tax_rate}%</span>
                              </div>
                            )}

                            {renderLimits(tax)}

                            <div className="tc-card__detail">
                              <span className="tc-card__label">{t('Applies to:')}</span>
                              <span className="tc-card__value">{t(tax.applies_to)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {taxes.length === 0 && (
                <div className="empty">
                  <p>{t('No tax configurations found')}</p>
                  {canManage && (
                    <button onClick={() => setShowTaxModal(true)} className="btn btn--primary">
                      {t('Create First Tax')}
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>

      {/* TAX MODAL */}
      {showTaxModal && (
        <Modal open title={editingTax ? t('Edit Tax Configuration') : t('Create Tax Configuration')} onClose={() => { setShowTaxModal(false); resetTaxForm(); }} size="lg">

            <form onSubmit={handleSubmitTax} className="tc-dialog">
              <div className="tc-section">
                <h3>{t('Basic Information')}</h3>
                <div className="tc-form-row">
                  <div className="tc-field">
                    <label className="field-label">{t('Tax Name *')}</label>
                    <input className="input"
                      type="text"
                      value={taxForm.tax_name}
                      onChange={(e) => setTaxForm({...taxForm, tax_name: e.target.value})}
                      required
                      placeholder={t('e.g., Federal Income Tax')}
                    />
                  </div>
                  <div className="tc-field">
                    <label className="field-label">{t('Tax Code *')}</label>
                    <input className="input"
                      type="text"
                      value={taxForm.tax_code}
                      onChange={(e) => setTaxForm({...taxForm, tax_code: e.target.value})}
                      required
                      placeholder={t('e.g., FIT')}
                    />
                  </div>
                </div>

                <div className="tc-field">
                  <label className="field-label">{t('Description')}</label>
                  <textarea className="textarea"
                    value={taxForm.description}
                    onChange={(e) => setTaxForm({...taxForm, description: e.target.value})}
                    rows="3"
                    placeholder={t('Optional description...')}
                  />
                </div>
              </div>

              <div className="tc-section">
                <h3>{t('Tax Configuration')}</h3>
                <div className="tc-form-row">
                  <div className="tc-field">
                    <label className="field-label">{t('Tax Type *')}</label>
                    <select className="select"
                      value={taxForm.tax_type}
                      onChange={(e) => {
                        setTaxForm({...taxForm, tax_type: e.target.value});
                        if (e.target.value === 'tiered' && taxBrackets.length === 0) {
                          setTaxBrackets([
                            { bracket_order: 1, min_amount: '0', max_amount: '', tax_rate: '', description: '' }
                          ]);
                        }
                      }}
                      required
                    >
                      <option value="percentage">{t('Percentage (Simple Rate)')}</option>
                      <option value="fixed">{t('Fixed Amount')}</option>
                      <option value="tiered">{t('Tiered/Progressive (Ranges)')}</option>
                    </select>
                    <small className="field-hint">
                      {taxForm.tax_type === 'percentage' && t('Single tax rate applied to income')}
                      {taxForm.tax_type === 'fixed' && t('Fixed amount deducted')}
                      {taxForm.tax_type === 'tiered' && t('Multiple tax brackets with different rates')}
                    </small>
                  </div>

                  <div className="tc-field">
                    <label className="field-label">{t('Currency *')}</label>
                    <select className="select"
                      value={taxForm.currency}
                      onChange={(e) => setTaxForm({...taxForm, currency: e.target.value})}
                      required
                    >
                      <option value="SRD">SRD</option>
                      <option value="USD">USD</option>
                    </select>
                  </div>
                </div>

                {/* Simple Rate (percentage or fixed) */}
                {taxForm.tax_type !== 'tiered' && (
                  <div className="tc-form-row">
                    {taxForm.tax_type === 'percentage' && (
                      <div className="tc-field">
                        <label className="field-label">{t('Tax Rate (%) *')}</label>
                        <input className="input"
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={taxForm.tax_rate}
                          onChange={(e) => setTaxForm({...taxForm, tax_rate: e.target.value})}
                          required
                        />
                      </div>
                    )}

                    {taxForm.tax_type === 'fixed' && (
                      <div className="tc-field">
                        <label htmlFor="tax-fixed-amount">{t('Amount ({currency}) *', { currency: taxForm.currency })}</label>
                        <input className="input"
                          id="tax-fixed-amount"
                          type="number"
                          step="0.01"
                          min="0.01"
                          value={taxForm.tax_rate}
                          onChange={(e) => setTaxForm({...taxForm, tax_rate: e.target.value})}
                          required
                        />
                        <small className="field-hint">{t('Deducted from each payroll')}</small>
                      </div>
                    )}

                    <div className="tc-field">
                      <label className="field-label">{t('Applies To')}</label>
                      <select className="select"
                        value={taxForm.applies_to}
                        onChange={(e) => setTaxForm({...taxForm, applies_to: e.target.value})}
                      >
                        <option value="gross">{t('Gross Pay')}</option>
                        <option value="net">{t('Net Pay')}</option>
                        <option value="base">{t('Base Salary')}</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Tax Brackets for Progressive Tax */}
                {taxForm.tax_type === 'tiered' && (
                  <div className="tc-brackets">
                    <div className="tc-brackets__head">
                      <h4>{t('Tax Brackets (Progressive Ranges)')} · {t('Brackets for {year}', { year: taxYear || '—' })}</h4>
                      <button 
                        type="button" 
                        onClick={addBracket}
                        className="btn btn--secondary"
                      >
                        <Plus aria-hidden="true" /> {t('Add Bracket')}
                      </button>
                    </div>

                    <div className="tc-brackets__list">
                      {taxBrackets.map((bracket, index) => (
                        <div key={index} className="tc-bracket">
                          <div className="tc-bracket__number">
                            {t('Bracket {n}', { n: bracket.bracket_order })}
                          </div>
                          
                          <div className="tc-bracket__fields">
                            <div className="tc-field">
                              <label className="field-label">{t('Min Amount *')}</label>
                              <input className="input"
                                type="number"
                                step="0.01"
                                min="0"
                                value={bracket.min_amount}
                                onChange={(e) => updateBracket(index, 'min_amount', e.target.value)}
                                required
                                placeholder={t('From')}
                              />
                            </div>

                            <div className="tc-field">
                              <label className="field-label">{t('Max Amount')}</label>
                              <input className="input"
                                type="number"
                                step="0.01"
                                min="0"
                                value={bracket.max_amount}
                                onChange={(e) => updateBracket(index, 'max_amount', e.target.value)}
                                placeholder={t('To (leave empty for ∞)')}
                              />
                            </div>

                            <div className="tc-field">
                              <label className="field-label">{t('Tax Rate (%) *')}</label>
                              <input className="input"
                                type="number"
                                step="0.01"
                                min="0"
                                max="100"
                                value={bracket.tax_rate}
                                onChange={(e) => updateBracket(index, 'tax_rate', e.target.value)}
                                required
                                placeholder={t('Rate')}
                              />
                            </div>

                            <div className="tc-field tc-field--grow">
                              <label className="field-label">{t('Description (Optional)')}</label>
                              <input className="input"
                                type="text"
                                value={bracket.description}
                                onChange={(e) => updateBracket(index, 'description', e.target.value)}
                                placeholder={t('e.g., Low income bracket')}
                              />
                            </div>

                            <button
                              type="button"
                              onClick={() => removeBracket(index)}
                              className="btn btn--secondary btn--icon btn--danger-text"
                              disabled={taxBrackets.length === 1}
                              title={t('Remove bracket')}
                              aria-label={t('Remove bracket')}
                            >
                              <Trash2 aria-hidden="true" />
                            </button>
                          </div>

                          <div className="tc-bracket__preview">
                            {bracket.min_amount && bracket.tax_rate && (
                              <span>
                                {taxForm.currency} {parseFloat(bracket.min_amount).toLocaleString(locale)} 
                                {bracket.max_amount ? ` - ${parseFloat(bracket.max_amount).toLocaleString(locale)}` : '+'} 
                                → {bracket.tax_rate}%
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="tc-help">
                      <p><Lightbulb aria-hidden="true" /> <strong>{t('Example:')}</strong></p>
                      <ul>
                        <li>{t('Bracket 1: $0 - $10,000 → 5%')}</li>
                        <li>{t('Bracket 2: $10,001 - $25,000 → 10%')}</li>
                        <li>{t('Bracket 3: $25,001 - $50,000 → 15%')}</li>
                        <li>{t('Bracket 4: $50,001+ → 20%')}</li>
                      </ul>
                    </div>
                  </div>
                )}

                {PAYROLL_OWN[editingTax?.tax_code] ? (
                  <p className="tc-note">{t(PAYROLL_OWN[editingTax.tax_code])}</p>
                ) : (
                  <div className="tc-form-row">
                    <div className="tc-field">
                      <label htmlFor="tax-min-amount">{t('Minimum base ({currency})', { currency: taxForm.currency })}</label>
                      <input className="input"
                        id="tax-min-amount"
                        type="number"
                        step="0.01"
                        min="0"
                        value={taxForm.min_amount}
                        onChange={(e) => setTaxForm({...taxForm, min_amount: e.target.value})}
                      />
                      <small className="field-hint">{t('No tax while the base is below this amount. Empty: no minimum.')}</small>
                    </div>
                    <div className="tc-field">
                      <label htmlFor="tax-max-amount">{t('Maximum tax ({currency})', { currency: taxForm.currency })}</label>
                      <input className="input"
                        id="tax-max-amount"
                        type="number"
                        step="0.01"
                        min="0"
                        value={taxForm.max_amount}
                        onChange={(e) => setTaxForm({...taxForm, max_amount: e.target.value})}
                      />
                      <small className="field-hint">{t('The most this tax takes from one payroll. Empty: no maximum.')}</small>
                    </div>
                  </div>
                )}

                <div className="tc-field">
                  <label className="field-label">{t('Calculation Order')}</label>
                  <input className="input"
                    type="number"
                    min="0"
                    value={taxForm.calculation_order}
                    onChange={(e) => setTaxForm({...taxForm, calculation_order: e.target.value})}
                  />
                  <small className="field-hint">{t('Lower numbers are calculated first')}</small>
                </div>
              </div>

              <div className="tc-section">
                <h3>{t('Employer Contribution (Optional)')}</h3>
                <div className="tc-field">
                  <label className="tc-check">
                    <input
                      type="checkbox"
                      checked={taxForm.is_employer_contribution}
                      onChange={(e) => setTaxForm({...taxForm, is_employer_contribution: e.target.checked})}
                    />
                    <span>{t('Employer also contributes to this tax')}</span>
                  </label>
                </div>

                {taxForm.is_employer_contribution && (
                  <div className="tc-field">
                    <label className="field-label">{t('Employer Rate (%)')}</label>
                    <input className="input"
                      type="number"
                      step="0.01"
                      value={taxForm.employer_rate}
                      onChange={(e) => setTaxForm({...taxForm, employer_rate: e.target.value})}
                    />
                  </div>
                )}
              </div>

              <div className="tc-section">
                <h3>{t('Settings')}</h3>
                <div className="tc-field">
                  <label className="tc-check">
                    <input
                      type="checkbox"
                      checked={taxForm.is_mandatory}
                      onChange={(e) => setTaxForm({...taxForm, is_mandatory: e.target.checked})}
                    />
                    <span>{t('Mandatory Tax (cannot be opted out)')}</span>
                  </label>
                </div>
                <div className="tc-field">
                  <label className="tc-check">
                    <input
                      type="checkbox"
                      checked={taxForm.is_active}
                      onChange={(e) => setTaxForm({...taxForm, is_active: e.target.checked})}
                    />
                    <span>{t('Active')}</span>
                  </label>
                </div>
              </div>

              <div className="tc-form-actions">
                <button 
                  type="button" 
                  onClick={() => {
                    setShowTaxModal(false);
                    resetTaxForm();
                  }}
                  className="btn btn--secondary"
                >
                  {t('Cancel')}
                </button>
                <button type="submit" className="btn btn--primary" disabled={loading}>
                  {loading ? t('Saving...') : editingTax ? t('Update Tax') : t('Create Tax')}
                </button>
              </div>
            </form>
        </Modal>
      )}
    </div>
  );
};

export default TaxConfiguration;